import React, { useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Smartphone, Loader2, CheckCircle, XCircle, ArrowLeft, Send, Download } from 'lucide-react';
import { createDeposit, pollDeposit, createPayout, getPayoutStatus } from '../../services/pawapayService';
import { useCountry } from '../../contexts/CountryContext';

const DOTS = ['bg-red-500', 'bg-blue-500', 'bg-emerald-500', 'bg-amber-500'];

const StatusPill = ({ status }) => {
  if (!status) return null;
  const done = status === 'COMPLETED';
  const failed = status === 'FAILED' || status === 'REJECTED' || status === 'TIMEOUT';
  const color = done ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
    : failed ? 'text-red-700 bg-red-50 border-red-200'
    : 'text-amber-700 bg-amber-50 border-amber-200';
  return (
    <span data-testid="sandbox-status-pill" className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-sm font-semibold ${color}`}>
      {done ? <CheckCircle className="w-4 h-4" /> : failed ? <XCircle className="w-4 h-4" /> : <Loader2 className="w-4 h-4 animate-spin" />}
      {status}
    </span>
  );
};

const PawaPayTestPage = () => {
  const ctx = useCountry();
  const providers = (ctx?.providers || []).map((p, i) => ({ id: p.id, label: p.label, dot: DOTS[i % DOTS.length] }));
  const [mode, setMode] = useState('deposit'); // 'deposit' | 'payout'
  const [method, setMethod] = useState('');
  const [amount, setAmount] = useState('1500');
  const [phone, setPhone] = useState('0999123456');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const [message, setMessage] = useState('');
  const [txnId, setTxnId] = useState('');

  const reset = () => { setStatus(null); setMessage(''); setTxnId(''); };

  React.useEffect(() => {
    if (!method && providers.length) setMethod(providers[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providers.length]);

  const runDeposit = async () => {
    reset(); setBusy(true); setStatus('STARTING'); setMessage('Requesting payment…');
    try {
      const dep = await createDeposit({
        amount, phoneNumber: phone, provider: method, country: ctx?.detectedIso3, description: 'Sandbox test deposit',
      });
      setTxnId(dep.depositId);
      setStatus(dep.status || 'ACCEPTED');
      setMessage('Approve the mobile-money prompt on your phone…');
      const final = await pollDeposit(dep.depositId, { onUpdate: (s) => s && setStatus(s) });
      setStatus(final);
      setMessage(final === 'COMPLETED' ? 'Payment received 🎉'
        : final === 'FAILED' ? 'Payment failed.'
        : final === 'IN_RECONCILIATION' ? 'Payment is being reconciled.'
        : 'Still processing — check again shortly.');
    } catch (err) {
      setStatus('REJECTED');
      const m = err?.detail?.failureReason?.failureMessage || err?.detail?.message || err?.detail || 'Could not start payment';
      setMessage(typeof m === 'string' ? m : JSON.stringify(m));
    } finally {
      setBusy(false);
    }
  };

  const runPayout = async () => {
    reset(); setBusy(true); setStatus('STARTING'); setMessage('Sending payout…');
    try {
      const po = await createPayout({
        amount, phoneNumber: phone, provider: method, country: ctx?.detectedIso3, description: 'Sandbox test payout',
      });
      setTxnId(po.payoutId);
      setStatus(po.status || 'ACCEPTED');
      setMessage('Payout submitted. Checking status…');
      // simple poll for payout
      let final = po.status;
      for (let i = 0; i < 20; i++) {
        await new Promise((r) => setTimeout(r, 4000));
        const res = await getPayoutStatus(po.payoutId);
        if (res.status) { final = res.status; setStatus(final); }
        if (['COMPLETED', 'FAILED', 'IN_RECONCILIATION'].includes(final)) break;
      }
      setMessage(final === 'COMPLETED' ? 'Payout completed 🎉' : final === 'FAILED' ? 'Payout failed.' : 'Payout is processing.');
    } catch (err) {
      setStatus('REJECTED');
      const m = err?.detail?.failureReason?.failureMessage || err?.detail?.message || err?.detail || 'Could not start payout';
      setMessage(typeof m === 'string' ? m : JSON.stringify(m));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-900 via-emerald-950 to-gray-950 py-10 px-4">
      <div className="max-w-md mx-auto">
        <button
          onClick={() => window.history.back()}
          data-testid="sandbox-back-btn"
          className="flex items-center gap-1.5 text-emerald-200 text-sm mb-5 active:scale-95 transition-transform"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <Card className="p-6 bg-white/95 backdrop-blur rounded-3xl shadow-2xl">
          <div className="flex items-center gap-3 mb-1">
            <span className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
              <Smartphone className="w-5 h-5 text-emerald-700" />
            </span>
            <div>
              <h1 className="text-xl font-extrabold text-gray-900">Mobile Money Sandbox</h1>
              <p className="text-xs text-gray-500">PawaPay test · {ctx?.country ? `${ctx.country.name} (${ctx.currency})` : 'Select country'}</p>
            </div>
          </div>

          {/* Country picker */}
          <label className="block text-sm font-semibold text-gray-700 mb-1 mt-4">Country</label>
          <select
            data-testid="sandbox-country-select"
            value={ctx?.detectedIso3 || ''}
            onChange={(e) => { ctx?.setCountryOverride(e.target.value); setMethod(''); reset(); }}
            className="w-full h-11 px-3 rounded-xl border border-gray-200 text-sm outline-none focus:border-emerald-500"
          >
            {(ctx?.countries || []).map((c) => (
              <option key={c.code} value={c.code}>{c.flag} {c.name} ({c.currency})</option>
            ))}
          </select>

          {/* Mode toggle */}
          <div className="grid grid-cols-2 gap-2 my-4 p-1 bg-gray-100 rounded-xl">
            <button
              data-testid="sandbox-mode-deposit"
              onClick={() => { setMode('deposit'); reset(); }}
              className={`flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-semibold transition-all ${mode === 'deposit' ? 'bg-white shadow text-emerald-700' : 'text-gray-500'}`}
            >
              <Download className="w-4 h-4" /> Collect (deposit)
            </button>
            <button
              data-testid="sandbox-mode-payout"
              onClick={() => { setMode('payout'); reset(); }}
              className={`flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-semibold transition-all ${mode === 'payout' ? 'bg-white shadow text-emerald-700' : 'text-gray-500'}`}
            >
              <Send className="w-4 h-4" /> Payout
            </button>
          </div>

          {/* Provider */}
          <label className="block text-sm font-semibold text-gray-700 mb-2">Provider</label>
          <div className="grid grid-cols-2 gap-2 mb-4">
            {providers.map((p) => (
              <button
                key={p.id}
                data-testid={`sandbox-provider-${p.id}`}
                onClick={() => setMethod(p.id)}
                className={`flex items-center gap-2 border-2 rounded-xl px-3 py-2.5 text-sm font-semibold transition-all ${method === p.id ? 'border-emerald-500 bg-emerald-50 text-emerald-800' : 'border-gray-200 text-gray-600'}`}
              >
                <span className={`w-2.5 h-2.5 rounded-full ${p.dot}`} /> {p.label}
              </button>
            ))}
          </div>

          {/* Amount */}
          <label className="block text-sm font-semibold text-gray-700 mb-2">Amount ({ctx?.currency || 'MWK'})</label>
          <Input
            data-testid="sandbox-amount-input"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="mb-4 text-lg"
            placeholder="1500"
          />

          {/* Phone */}
          <label className="block text-sm font-semibold text-gray-700 mb-2">Mobile number</label>
          <Input
            data-testid="sandbox-phone-input"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="mb-1 text-lg"
            placeholder="0999123456"
          />
          <p className="text-xs text-gray-500 mb-5">Local (0999…) or full (265999…) format works.</p>

          <Button
            data-testid="sandbox-submit-btn"
            onClick={mode === 'deposit' ? runDeposit : runPayout}
            disabled={busy || !amount || !phone}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-base font-bold rounded-xl"
          >
            {busy ? (<><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Processing…</>)
              : mode === 'deposit' ? `Collect MWK ${Number(amount || 0).toLocaleString()}`
              : `Pay out MWK ${Number(amount || 0).toLocaleString()}`}
          </Button>

          {(status || message) && (
            <div className="mt-5 p-4 rounded-2xl bg-gray-50 border border-gray-200 text-center" data-testid="sandbox-result">
              <StatusPill status={status} />
              {message && <p className="text-sm text-gray-700 mt-2">{message}</p>}
              {txnId && <p className="text-[11px] text-gray-400 mt-2 break-all">ID: {txnId}</p>}
            </div>
          )}
        </Card>

        <p className="text-center text-emerald-200/70 text-xs mt-5">
          Sandbox environment — no real money moves.
        </p>
      </div>
    </div>
  );
};

export default PawaPayTestPage;
